import { INDUSTRIES, NAME_MAX_LENGTH, ORG_SIZES, type IndustryKey, type OrgSizeKey } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Input, Label, Select } from "@sagot/ui";
import type { SyntheticEvent } from "react";
import { CountrySelect } from "../lib/region-pickers";
import { PillChoice } from "./components/pill-choice";
import { REGISTER_INDUSTRY_ORDER } from "./register-constants";
import { IndustryGlyph } from "./register-icons";
import { PaneTitle, RegisterNav, RegisterPane } from "./register-pane";
import { isOrgNameOk, isOrgOk } from "./register-rules";
import { useFieldSeen } from "./use-field-seen";
import { WorkspaceUrlHint } from "./workspace-url-hint";

const ORG_ERROR_ID = "r-org-error";

const SIZE_LABEL: Record<OrgSizeKey, string> = {
  lt_200: "sizeUnder200",
  "200_500": "size200to500",
  "501_1500": "size501to1500",
  "1501_5000": "size1501to5000",
  gt_5000: "sizeOver5000",
};

const ORDERED_INDUSTRIES: readonly IndustryKey[] = REGISTER_INDUSTRY_ORDER.filter((industry) => INDUSTRIES.includes(industry));

interface RegisterOrgStepProps {
  orgName: string;
  industry: IndustryKey | null;
  size: OrgSizeKey;
  country: string;
  onChange: (patch: { orgName?: string; industry?: IndustryKey; size?: OrgSizeKey; country?: string }) => void;
  error: string | null;
  back: boolean;
  onBack: () => void;
  onNext: () => void;
}

/** Step 3 (mockup pane 4): organization name with the workspace address, industry chips, size and country. */
export function RegisterOrgStep({ orgName, industry, size, country, onChange, error, back, onBack, onNext }: RegisterOrgStepProps) {
  const { t } = useTranslation();
  const ready = isOrgOk(orgName, industry);
  // Continue is off while the name is missing; the field says why once the visitor has left it (or has picked an industry and no name).
  const orgField = useFieldSeen();
  const orgBad = !isOrgNameOk(orgName) && (orgField.seen || industry !== null);

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (ready) onNext();
  };

  return (
    <RegisterPane back={back} onSubmit={submit}>
      <PaneTitle>{t("auth.register.yourOrganization")}</PaneTitle>
      <p className="a-sub">{t("auth.register.orgSub")}</p>
      {error ? (
        <p role="alert" className="r-error">
          {error}
        </p>
      ) : null}
      <div className="a-fld">
        <Label htmlFor="r-org">{t("auth.register.orgName")}</Label>
        <Input
          id="r-org"
          autoComplete="organization"
          placeholder={t("auth.register.orgPlaceholder")}
          maxLength={NAME_MAX_LENGTH}
          value={orgName}
          invalid={orgBad || undefined}
          aria-describedby={orgBad ? ORG_ERROR_ID : undefined}
          onChange={(event) => {
            onChange({ orgName: event.target.value });
          }}
          onBlur={orgField.onBlur}
          onKeyDown={(event) => {
            if (event.key === "Enter") orgField.markSeen();
          }}
        />
        {orgBad ? (
          <p id={ORG_ERROR_ID} className="hint hint-err" role="alert">
            {t("auth.register.orgNameRequired")}
          </p>
        ) : null}
        <WorkspaceUrlHint name={orgName} />
      </div>
      <div className="a-fld">
        <span className="label" id="r-industry-label">
          {t("auth.register.orgType")}
        </span>
        <PillChoice
          variant="chip"
          aria-labelledby="r-industry-label"
          value={industry}
          onChange={(value) => {
            onChange({ industry: value });
          }}
          options={ORDERED_INDUSTRIES.map((value) => ({
            value,
            label: t(`onboarding.industries.${value}`),
            glyph: <IndustryGlyph industry={value} />,
          }))}
        />
      </div>
      <div className="r-2">
        <Field className="a-fld" label={t("auth.register.size")} id="r-size">
          <Select
            value={size}
            onChange={(event) => {
              onChange({ size: event.target.value as OrgSizeKey });
            }}
          >
            {ORG_SIZES.map((value) => (
              <option key={value} value={value}>
                {t(`auth.register.${SIZE_LABEL[value]}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field className="a-fld r-country" label={t("auth.register.country")} id="r-country">
          <CountrySelect
            id="r-country"
            value={country}
            onChange={(value) => {
              onChange({ country: value });
            }}
          />
        </Field>
      </div>
      <RegisterNav backLabel={t("auth.register.back")} onBack={onBack}>
        <Button type="submit" size="lg" disabled={!ready}>
          {t("auth.register.continue")}
        </Button>
      </RegisterNav>
    </RegisterPane>
  );
}
